import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';

import Button from 'modules/_shared/components/Buttons';
import { setModal } from 'modules/_shared/redux/modalActions';
import { Council, Document } from 'types';
import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { updateCouncil } from 'modules/administratives/redux/councilActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import { addDocument } from 'modules/documents/redux/documentActions';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import { ScreenSizes } from 'constants/sizes';
import UploadDocument from 'modules/documents/components/UploadDocument';
import useGetDocumentData from 'modules/administratives/pages/CouncilDetails/hooks/useGetDocumentData';
import councilTemplateTypes from 'modules/administratives/pages/CouncilDetails/constants/councilTemplateTypes';
import signatureTypes from 'constants/signatureTypes';
import EmbedPDFViewer from 'modules/administratives/components/EmbedPDFViewer';
import RequestSignatureModal from '../RequestSignatureModal';

type EditCouncilActEditorProps = {
  size?: ScreenSizes;
  council: Council;
};

const EditCouncilActEditor: FC<EditCouncilActEditorProps> = ({ council }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const society = useSelector((state: State) => state.society.actualSociety);

  const [isLoading, setIsLoading] = useState(false);
  const [actFile, setActFile] = useState<File | undefined>(undefined);

  // Get template data for act
  const actTemplateData = useGetDocumentData({
    documentId: council.actDocument ?? null,
    templateId: council.actTemplate ?? null,
  });

  const { documentFrame: actDocumentFrame } = actTemplateData.data;

  const { isLoading: isLoadingActTemplate } = actTemplateData;

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const renderActPreview = () => {
    const actFileUrl = actFile ? URL.createObjectURL(actFile) : null;
    const actTemplateUrl = actDocumentFrame ?? null;

    const actPreviewUrl = actTemplateUrl ?? actFileUrl;

    return <EmbedPDFViewer fileUrl={actPreviewUrl ?? null} />;
  };

  const uploadDocument = async (
    file: File,
    documentName: string,
    description: string
  ): Promise<Document | null> => {
    try {
      const newDocument = {
        file,
        date: new Date(),
        size: file.size,
        name: documentName,
        author: user?._id,
        society: society?._id,
        fileType: fileTypes[file.type as keyof typeof fileTypes],
        category: documentTypes.DOCUMENTS,
        subcategory: 'COUNCIL',
        description,
      };

      const uploadedDocument = await store.dispatch(addDocument(newDocument));
      return uploadedDocument;
    } catch (error) {
      return null;
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const newCouncilData: Partial<Council> = {
        _id: council._id,
      };

      // Upload act document if file is selected
      if (actFile) {
        const uploadedActDocument = await uploadDocument(
          actFile,
          `Act-${council.name}-${new Date().toISOString().split('T')[0]}.pdf`,
          t('Council Act Document')
        );
        if (uploadedActDocument) {
          newCouncilData.actDocument = uploadedActDocument._id;
        }
      }

      // Only update if there are changes
      if (actFile) {
        await store.dispatch(updateCouncil(newCouncilData));
        await store.dispatch(addAlert(alertBodyTypes.COUNCIL_UPDATED));
      }

      dispatch(setModal(null));
    } catch (error) {
      await store.dispatch(addAlert(alertBodyTypes.ERROR_UPDATING_COUNCIL));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestActSignature = () => {
    if (!council._id || !actTemplateData.data.document) {
      return;
    }

    dispatch(
      setModal(
        <RequestSignatureModal
          councilId={council._id}
          document={actTemplateData.data.document}
          templateType={councilTemplateTypes.ACT}
          signatureType={signatureTypes.COUNCIL_ACT}
        />
      )
    );
  };

  return (
    <>
      <Modal.Header>
        <h4 className="modal-title mb-0">
          <span>{t('ReplaceAct')}</span>
        </h4>

        <a className="close cursor-pointer" onClick={handleClose}>
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        <div className="nk-modal">
          <p className="text-muted mb-3">{t('ReplaceCouncilActDescription')}</p>

          <div className="mb-3">
            <UploadDocument
              label={t('SelectFile')}
              labelClassName="h6"
              file={actFile}
              setFile={setActFile}
              idIndex={1}
            />
          </div>

          {/* Show template preview or uploaded file preview */}
          <div className="mt-4">
            <label className="form-label h6">{t('Preview')}</label>
            {renderActPreview()}
          </div>

          <div className="mt-3 d-flex justify-content-end">
            <Button onClick={handleRequestActSignature}>
              <em className="icon ni ni-edit-alt mr-1" />
              {t('RequestSignature')}
            </Button>
          </div>

          {(isLoading || isLoadingActTemplate) && <CustomLoading />}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!actFile || isLoading}
        >
          {isLoading ? t('Saving...') : t('Save')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default EditCouncilActEditor;
